// Single source of truth for keys: ID, hitbox position, optional overlay
// for visual press-state, SDL scancode for emulator, web key for keyboard.
//
// Positions are percentages of the device-frame (which is 1:1 aspect-ratio
// holding the 1254x1254 overlay image). Recalibration: run `pnpm dev` with
// `?debug=1` for outlines or `?calibrate=1` to log click/drag percentages.

export interface HitboxPoint {
  /** X coordinate as a CSS % string, relative to this key's hitbox rect. */
  readonly x: string;
  /** Y coordinate as a CSS % string, relative to this key's hitbox rect. */
  readonly y: string;
}

export interface KeyDef {
  /** Stable key id (used by worker, overlay mapping, debug labels). */
  readonly id: string;
  /** SDL2 scancode the emulator will receive (0 if non-standard / synthesized). */
  readonly sdlScancode: number;
  /** Physical-keyboard key that triggers this Cybiko key. */
  readonly webKey: string;
  /** Hitbox position as CSS % strings, relative to .device-frame. */
  readonly position: {
    readonly top: string;
    readonly left: string;
    readonly width: string;
    readonly height: string;
  };
  /** Optional: non-rectangular input shape, relative to the position rect. */
  readonly polygon?: readonly HitboxPoint[];
  /** Optional: press-state overlay file (without .webp extension). */
  readonly overlay?: string;
  /** Optional: shape hint for hitbox styling (round | pill). */
  readonly shape?: 'round' | 'pill';
  /** Optional: false for keyboard-only keys that should not render a hitbox. */
  readonly renderHitbox?: false;
  /** Optional: human-readable label for accessibility. */
  readonly label?: string;
}

// SDL_SCANCODE_* constants used here:
// 4..29   = A..Z
// 30..39  = 1..0
// 40      = Return/Enter
// 41      = Escape
// 42      = Backspace
// 43      = Tab
// 44      = Space
// 58..69  = F1..F12
// 73      = Insert
// 76      = Delete
// 79..82  = Right/Left/Down/Up

export const KEYS: readonly KeyDef[] = [
  // Esc / power: powers on while off, sends Escape on short press while
  // running, and powers off when held.
  {
    id: 'esc',
    sdlScancode: 41,
    webKey: 'Escape',
    label: 'Escape / power',
    overlay: 'cybiko-esc',
    shape: 'round',
    position: { top: '88.185%', left: '11.000%', width: '19.926%', height: '11.630%' },
    polygon: [
      { x: '3.717%', y: '26.115%' },
      { x: '0.000%', y: '29.936%' },
      { x: '1.487%', y: '38.217%' },
      { x: '4.461%', y: '54.777%' },
      { x: '8.922%', y: '80.892%' },
      { x: '11.524%', y: '100.000%' },
      { x: '15.613%', y: '99.363%' },
      { x: '20.074%', y: '100.000%' },
      { x: '24.907%', y: '99.363%' },
      { x: '34.944%', y: '94.905%' },
      { x: '44.238%', y: '89.809%' },
      { x: '54.647%', y: '80.255%' },
      { x: '65.428%', y: '70.701%' },
      { x: '73.606%', y: '61.783%' },
      { x: '82.528%', y: '49.682%' },
      { x: '89.591%', y: '38.217%' },
      { x: '97.026%', y: '26.752%' },
      { x: '99.628%', y: '17.197%' },
      { x: '100.000%', y: '8.917%' },
      { x: '97.398%', y: '4.459%' },
      { x: '93.309%', y: '1.274%' },
      { x: '85.130%', y: '0.000%' },
      { x: '73.606%', y: '3.185%' },
      { x: '61.338%', y: '5.732%' },
      { x: '44.981%', y: '9.554%' },
      { x: '30.855%', y: '13.376%' },
      { x: '17.472%', y: '19.108%' },
    ],
  },

  // 7 dome function buttons across the top. These final traced hitboxes were
  // landed from the interactive calibration export and lock to the overlay art.
  {
    id: 'f1',
    sdlScancode: 58,
    webKey: 'F2',
    label: 'Function 1',
    overlay: 'cybiko-top-left',
    shape: 'round',
    position: { top: '6.481%', left: '20.037%', width: '5.259%', height: '5.259%' },
  },
  {
    id: 'f2',
    sdlScancode: 59,
    webKey: 'F3',
    label: 'Function 2',
    overlay: 'cybiko-top-second-from-left',
    shape: 'round',
    position: { top: '8.630%', left: '30.037%', width: '5.185%', height: '5.185%' },
  },
  {
    id: 'f3',
    sdlScancode: 60,
    webKey: 'F4',
    label: 'Function 3',
    overlay: 'cybiko-top-third-from-left',
    shape: 'round',
    position: { top: '10.111%', left: '39.519%', width: '5.333%', height: '5.333%' },
  },
  {
    id: 'f4',
    sdlScancode: 61,
    webKey: 'F5',
    label: 'Function 4',
    overlay: 'cybiko-top-middle',
    shape: 'round',
    position: { top: '9.963%', left: '49.519%', width: '5.185%', height: '5.185%' },
  },
  {
    id: 'f5',
    sdlScancode: 62,
    webKey: 'F6',
    label: 'Function 5',
    overlay: 'cybiko-top-third-from-right',
    shape: 'round',
    position: { top: '9.000%', left: '59.222%', width: '5.333%', height: '5.333%' },
  },
  {
    id: 'f6',
    sdlScancode: 63,
    webKey: 'F7',
    label: 'Function 6',
    overlay: 'cybiko-top-second-from-right',
    shape: 'round',
    position: { top: '7.222%', left: '69.000%', width: '5.185%', height: '5.185%' },
  },
  {
    id: 'f7',
    sdlScancode: 64,
    webKey: 'F8',
    label: 'Function 7',
    overlay: 'cybiko-top-right',
    shape: 'round',
    position: { top: '4.259%', left: '78.778%', width: '4.963%', height: '4.963%' },
  },

  // D-pad: the visible cropped rubber surface clicks as up. Keyboard
  // left/right/down still send their real direction ids and share this overlay.
  {
    id: 'up',
    sdlScancode: 82,
    webKey: 'ArrowUp',
    label: 'D-pad up',
    overlay: 'cybiko-dpad',
    shape: 'round',
    position: { top: '93.370%', left: '25.667%', width: '26.815%', height: '6.593%' },
    polygon: [
      { x: '29.282%', y: '0.000%' },
      { x: '23.481%', y: '25.843%' },
      { x: '16.851%', y: '47.191%' },
      { x: '10.221%', y: '66.292%' },
      { x: '6.354%', y: '80.899%' },
      { x: '0.000%', y: '96.629%' },
      { x: '8.564%', y: '100.000%' },
      { x: '19.337%', y: '98.876%' },
      { x: '32.320%', y: '97.753%' },
      { x: '42.541%', y: '98.876%' },
      { x: '54.696%', y: '100.000%' },
      { x: '66.298%', y: '100.000%' },
      { x: '78.729%', y: '100.000%' },
      { x: '87.569%', y: '98.876%' },
      { x: '100.000%', y: '100.000%' },
      { x: '95.028%', y: '78.652%' },
      { x: '90.055%', y: '60.674%' },
      { x: '83.978%', y: '44.944%' },
      { x: '75.967%', y: '30.337%' },
      { x: '65.470%', y: '17.978%' },
      { x: '55.249%', y: '7.865%' },
      { x: '42.818%', y: '0.000%' },
    ],
  },
  {
    id: 'down',
    sdlScancode: 81,
    webKey: 'ArrowDown',
    label: 'D-pad down',
    overlay: 'cybiko-dpad',
    renderHitbox: false,
    shape: 'round',
    position: { top: '92.0%', left: '30.5%', width: '7.0%', height: '5.0%' },
  },
  {
    id: 'left',
    sdlScancode: 80,
    webKey: 'ArrowLeft',
    label: 'D-pad left',
    overlay: 'cybiko-dpad',
    renderHitbox: false,
    shape: 'round',
    position: { top: '87.5%', left: '24.0%', width: '6.0%', height: '5.5%' },
  },
  {
    id: 'right',
    sdlScancode: 79,
    webKey: 'ArrowRight',
    label: 'D-pad right',
    overlay: 'cybiko-dpad',
    renderHitbox: false,
    shape: 'round',
    position: { top: '87.5%', left: '38.5%', width: '6.0%', height: '5.5%' },
  },

  // Del — upper-right pill (above Ins)
  {
    id: 'del',
    sdlScancode: 76,
    webKey: 'Delete',
    label: 'Delete',
    overlay: 'cybiko-del',
    shape: 'pill',
    position: { top: '86.704%', left: '60.556%', width: '13.852%', height: '7.778%' },
    polygon: [
      { x: '38.503%', y: '9.524%' },
      { x: '24.064%', y: '19.048%' },
      { x: '13.904%', y: '32.381%' },
      { x: '5.882%', y: '47.619%' },
      { x: '0.000%', y: '67.619%' },
      { x: '2.139%', y: '85.714%' },
      { x: '11.765%', y: '95.238%' },
      { x: '22.995%', y: '100.000%' },
      { x: '38.503%', y: '93.333%' },
      { x: '51.872%', y: '90.476%' },
      { x: '69.519%', y: '79.048%' },
      { x: '83.422%', y: '68.571%' },
      { x: '93.583%', y: '53.333%' },
      { x: '100.000%', y: '35.238%' },
      { x: '98.396%', y: '20.000%' },
      { x: '91.444%', y: '9.524%' },
      { x: '78.610%', y: '3.810%' },
      { x: '59.893%', y: '0.000%' },
    ],
  },

  // Ins — lower-right pill (below Del)
  {
    id: 'ins',
    sdlScancode: 73,
    webKey: 'Insert',
    label: 'Insert',
    overlay: 'cybiko-ins',
    shape: 'pill',
    position: { top: '95.148%', left: '65.667%', width: '18.519%', height: '4.815%' },
    polygon: [
      { x: '99.200%', y: '24.616%' },
      { x: '92.800%', y: '13.846%' },
      { x: '84.400%', y: '9.231%' },
      { x: '74.000%', y: '0.000%' },
      { x: '62.400%', y: '0.000%' },
      { x: '48.400%', y: '6.154%' },
      { x: '36.000%', y: '12.308%' },
      { x: '24.400%', y: '30.769%' },
      { x: '16.000%', y: '44.615%' },
      { x: '7.200%', y: '64.615%' },
      { x: '0.000%', y: '95.385%' },
      { x: '15.600%', y: '100.000%' },
      { x: '32.800%', y: '100.000%' },
      { x: '48.000%', y: '98.462%' },
      { x: '66.400%', y: '98.462%' },
      { x: '76.000%', y: '90.769%' },
      { x: '87.600%', y: '76.923%' },
      { x: '94.800%', y: '61.539%' },
      { x: '100.000%', y: '43.077%' },
    ],
  },

  // ? — tiny button far-right
  {
    id: 'question',
    sdlScancode: 56,
    webKey: '?',
    label: 'Question / help',
    overlay: 'cybiko-question',
    shape: 'round',
    position: { top: '94.333%', left: '88.333%', width: '5.667%', height: '5.667%' },
  },

  // The Cybiko has a full QWERTY keyboard below the d-pad that is
  // off-camera in this photo crop. Physical keyboard remains primary input
  // for letter / number keys via the global keydown handler in main.ts.
];

/** Lookup keys by web key for keyboard event dispatch (visible buttons). */
export const WEB_TO_KEY: ReadonlyMap<string, KeyDef> = new Map(
  KEYS.map((k) => [k.webKey, k]),
);

/** Lookup keys by id. */
export const KEY_BY_ID: ReadonlyMap<string, KeyDef> = new Map(
  KEYS.map((k) => [k.id, k]),
);

/**
 * Physical-keyboard → Cybiko key-id mapping. Includes all visible buttons
 * plus invisible keys (Enter, Tab, Space, letters, digits) that exist on
 * the real Cybiko keyboard but aren't shown in the photo crop. These let
 * the user type and navigate via their physical keyboard even when the
 * matching device button isn't clickable.
 */
const KEYBOARD_EXTRA: ReadonlyArray<readonly [string, string]> = [
  ['Enter', 'enter'],
  ['Tab', 'tab'],
  ['Backspace', 'backspace'],
  [' ', 'space'],
  ['Home', 'select'],
  ['End', 'help'],
  ['Shift', 'shift'],
  // Punctuation that maps to Cybiko keys
  ["'", 'apostrophe'],
  [',', 'comma'],
  ['.', 'period'],
  [';', 'semicolon'],
  ['/', 'slash'],
  ['-', 'minus'],
  ['=', 'equals'],
  ['[', 'openbracket'],
  [']', 'closebracket'],
  ['\\', 'backslash'],
  ['`', 'backtick'],
];

const letters: Array<readonly [string, string]> = [];
for (let i = 0; i < 26; i++) {
  const ch = String.fromCharCode(0x61 + i);
  letters.push([ch, ch]);
  // Uppercase mirrors (shift+letter still produces the lowercase id)
  letters.push([ch.toUpperCase(), ch]);
}
const digits: Array<readonly [string, string]> = [];
for (let i = 0; i <= 9; i++) {
  digits.push([String(i), String(i)]);
}

export const KEYBOARD_BINDINGS: ReadonlyMap<string, string> = new Map([
  ...KEYS.map((k) => [k.webKey, k.id] as const),
  ...KEYBOARD_EXTRA,
  ...letters,
  ...digits,
]);
